import React from "react";
import { FileText, Plus, UploadCloud, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatINR, resolveImg } from "@/lib/api";
import { PurchaseItemRow } from "@/pages/admin/purchases/PurchaseItemRow";

// "New Purchase" dialog: supplier/date/GST header, dynamic item rows, attachments, and the live total preview.
export const NewPurchaseDialog = ({
  open, onClose, form, setForm, suppliers, products, onAddItem, onSetItem, onSetWeightField,
  onSyncJewelleryWeights, onRemoveItem, previewSubtotal, previewGst, previewTotal,
  uploadingAttachment, createFileInputRef, onUploadAttachment, onRemoveAttachment, onSave, editing = null,
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent data-testid="purchase-form-dialog" className="max-w-3xl max-h-[88vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">{editing ? `Edit Purchase — ${editing.purchase_no}` : "New Purchase"}</DialogTitle>
        <DialogDescription>{editing
          ? "Update weights, carats, rates and labour charges. On save the linked inventory pieces (in stock) are re-priced and the Supplier + General ledgers receive the difference automatically. Sold pieces stay frozen."
          : "Record a purchase from a supplier — stock and the supplier ledger update automatically."}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Supplier *</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })} disabled={!!editing}>
              <SelectTrigger data-testid="purchase-supplier-select" className="mt-1"><SelectValue placeholder="Select supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">GST %</Label><Input type="number" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: e.target.value })} className="mt-1 font-mono" /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2"><Label className="text-sm font-medium">Items</Label><Button size="sm" variant="secondary" className="gap-1" onClick={onAddItem}><Plus className="h-3.5 w-3.5" /> Add Item</Button></div>
          {form.items.map((it, i) => (
            <PurchaseItemRow
              key={it._key ?? i} it={it} index={i} canRemove={form.items.length > 1} products={products}
              onSetItem={(patch) => onSetItem(i, patch)}
              onSetWeightField={(field, value) => onSetWeightField(i, field, value)}
              onSyncJewelleryWeights={(patch) => onSyncJewelleryWeights(i, patch)}
              onRemove={() => onRemoveItem(i)}
            />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Payment Method</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={1} className="mt-1" /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-sm font-medium">Bill / Challan Attachments</Label>
            <Button type="button" size="sm" variant="secondary" className="gap-1" disabled={uploadingAttachment} onClick={() => createFileInputRef.current?.click()}>
              <UploadCloud className="h-3.5 w-3.5" /> {uploadingAttachment ? "Uploading..." : "Upload File"}
            </Button>
            <input ref={createFileInputRef} type="file" accept="image/*,.pdf" multiple className="hidden" data-testid="purchase-attachment-input"
              onChange={(e) => { onUploadAttachment(e.target.files); e.target.value = ""; }} />
          </div>
          {form.attachments.length === 0 ? (
            <p className="text-xs text-muted-foreground">No attachments yet. Upload a supplier bill or challan photo (JPG, PNG or PDF).</p>
          ) : (
            <div className="space-y-1" data-testid="purchase-attachment-list">
              {form.attachments.map((a, i) => (
                <div key={a.url || i} className="flex items-center justify-between gap-2 rounded-md border border-border bg-[#F7F0E6] px-2 py-1.5 text-xs">
                  <a href={resolveImg(a.url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[hsl(var(--primary))] hover:underline truncate">
                    <FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{a.filename || "Attachment"}</span>
                  </a>
                  <Button type="button" size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={() => onRemoveAttachment(i)}><X className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-border bg-[#F4EBDD] p-3 flex items-center justify-between">
          <span className="text-sm">Subtotal {formatINR(previewSubtotal)} + GST {formatINR(previewGst)}</span>
          <span className="font-mono text-lg text-[hsl(var(--primary))]" data-testid="purchase-preview-total">{formatINR(previewTotal)}</span>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="purchase-save-button" onClick={onSave}>{editing ? "Save Changes & Re-sync" : "Record Purchase"}</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);
